// packages/api/src/hooks/useCepLookup.ts

import { useQuery } from '@tanstack/react-query';

import type { CepLookupResult } from '@alamo/types';

import { apiClient } from '../client';
import { queryKeys } from '../query-keys';

/**
 * Fetch address data by CEP through the backend proxy.
 *
 * Backend (GET /api/v1/registrations/cep/:cep) cascades BrasilAPI v2 → ViaCEP,
 * caches the result in Valkey for 30d, and resolves `cityId` via LocationsService
 * (IBGE match, with findCityByNameAndUf fallback).
 *
 * Returns `null` when the input is not a valid 8-digit CEP (keeps the hook
 * safe to call eagerly during form state transitions). Shared across the whole
 * app (quotation, profile, party, registration, block conference) — a general
 * address proxy, not registration-specific.
 */
export function useCepLookup(cep: string) {
  const cleanCep = (cep ?? '').replace(/\D/g, '');
  return useQuery({
    queryKey: queryKeys.cep.lookup(cleanCep),
    // `signal` is wired through to axios so that when the user edits the CEP
    // the in-flight request is aborted before a new one is fired — prevents
    // stale responses from overwriting a fresher cached result.
    queryFn: async ({ signal }) => {
      const response = await apiClient.get<{ data: CepLookupResult }>(
        `/registrations/cep/${cleanCep}`,
        { signal },
      );
      return response.data.data;
    },
    enabled: cleanCep.length === 8,
    staleTime: 30 * 60 * 1000, // 30 minutes — backend already caches 30d in Valkey
    retry: 1,
  });
}
